import api from '../../api'

// Utilities
import isEmpty from '../../utility/isEmpty'
import Logger from '../../utility/loggerUtil'

import { Cards, NcrType } from '../../constants.js'

export default {
  async getReferralInfo (context) {
    try {
      await context.dispatch('getCognitoAttributes')

      if (!isEmpty(context.state.ncrCognitoAttribute) ? context.state.ncrCognitoAttribute !== NcrType.customer && isEmpty(context.rootState.loans.loans) : isEmpty(context.rootState.loans.loans)) {
        await context.dispatch('loans/getLoans', undefined, { root: true }) // TODO cache getLoans call when vuex version is updated
      }

      const lmsId = context.rootGetters['loans/getActiveLms']

      const referralInfo = (await api.customer.getCustomerInfo(lmsId, undefined).catch(e => {
        Logger.error('Error getting referral info, trying again')
        Logger.debug(e)

        return api.customer.getCustomerInfo(lmsId, undefined)
      }))

      Logger.info('Got referral info')
      Logger.debug(referralInfo)

      if (!isEmpty(referralInfo)) {
        context.commit('setReferralInfo', referralInfo)
        context.commit('customer/setCustomerData', referralInfo, { root: true })
      }
    } catch (e) {
      Logger.error('Error getting referral info')
      Logger.debug(e)
      throw e
    }
  },

  async getBirdEyeReferralUrl (context) {
    try {
      const requestParameters = {
        firstName: context.rootState.customer.customerFirstName,
        lastName: context.rootState.customer.customerLastName,
        email: context.rootState.customer.email,
        phoneNumber: context.rootState.customer.phoneNumber.phoneNumber,
        shortCode: context.state.shortCode
      }
      const response = await api.referral.getBirdEyeReferralUrl(requestParameters)
      await context.commit('setReferralInfo', { birdEyeUrl: response.referralUrl })
    } catch (e) {
      Logger.error('Error on referral/getBirdEyeReferralUrl')
      Logger.debug(e)
      throw e
    }
  },

  async getReferralStats (context) {
    try {
      const response = await api.referral.getReferralStats()
      context.commit('setReferralStats', response)
    } catch (e) {
      Logger.error('Error on referral/getReferralStats')
      Logger.debug(e)
      throw e
    }
  },

  async getBalanceHistory (context) {
    try {
      const response = await api.referral.getBalanceHistory()
      context.commit('setBalanceHistory', response)
    } catch (e) {
      Logger.error('Error on referral/getBalanceHistory')
      Logger.debug(e)
      throw e
    }
  },

  async getCognitoAttributes (context) {
    try {
      const response = await api.user.getAttributes()
      context.commit('setReferralStatus', response)
    } catch (e) {
      Logger.error('Error on referral/getCognitoAttributes')
      Logger.debug(e)
      throw e
    }
  },

  /**
  * Gets the secure acceptance session data.
  * @returns {Promise<{acceptance: Object, acceptanceConfig: {formUrl: String}}>} The secure acceptance form data.
  */
  async getSecureAcceptData (context) {
    try {
      return await context.dispatch('cards/getCardFormKey', { transactionType: Cards.transactionType.refer.id }, { root: true })
    } catch (e) {
      Logger.error('Error getting card form info')
      Logger.debug(e)
      throw e
    }
  },

  /**
   * Gets the secure acceptance session status and saves result once found.
   * @returns {Promise<Boolean>} True if status was returned, false if no status was found.
   */
  async getSecureAcceptStatus (context, data) {
    try {
      const status = await api.referral.getSecureAcceptStatus(data.transactionUuid)

      if (isEmpty(status)) {
        Logger.debug('No card status found')
        return false
      }

      Logger.debug(`balance: ${context.state.currentBalance}`)

      context.commit('setDisburseCardData', {
        cardId: status.paymentToken,
        cardName: `${status.firstName} ${status.lastName}`,
        cardNumber: status.cardNumber,
        cardType: status.cardType,
        disbursementAmount: context.state.currentBalance
      })

      return true
    } catch (e) {
      Logger.error('Error getting card status')
      Logger.debug(e)

      throw e
    }
  },

  /**
   * Starts a balance disbursement to a card.
   * @returns {Promise<String>} Returns a message key to check the disbursements status.
   */
  async initiateDisbursement (context) {
    try {
      const response = await api.referral.initiateDisbursement(context.state.cardNumber, context.state.cardId)

      return response.messageKey
    } catch (e) {
      Logger.error('Error disbursing balance')
      Logger.debug(e)

      throw e
    }
  },

  /**
   * Checks the status of a disbursement to a card.
   * @returns {Promise<{status: String, error: String}>} Will return a status and possibly an error.
   */
  async checkDisbursementStatus (context, data) {
    try {
      return await api.referral.checkDisbursementStatus(data.messageKey)
    } catch (e) {
      Logger.error('Error checking disbursement status')
      Logger.debug(e)

      throw e
    }
  },

  async recordShareEvent (context, data) {
    try {
      const storeId = Object.keys(context.rootState.store.stores)[0]
      const storeInfo = !isEmpty(storeId) ? context.rootGetters['store/getStoreInfo'](storeId) : ''
      const storeNumber = !isEmpty(storeInfo) ? storeInfo.id : ''
      const storeName = !isEmpty(storeInfo) ? storeInfo.name : ''

      const name = context.rootState.customer.customerName
      const email = context.rootState.customer.email
      const phone = context.rootState.customer.phoneNumber.phoneNumber
      const shortCode = context.state.shortCode

      return await api.referral.recordShareEvent(name, email, phone, storeNumber, storeName, data.source, shortCode)
    } catch (e) {
      Logger.error('Error sending record share event.')
      Logger.debug(e)

      throw e
    }
  }
}
